import React, { memo, useState } from 'react';
import {
  Box, Button, Flex, Image, Stack, Text, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Select, useDisclosure, Badge
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { setCart } from '../features/cart/cartSlice';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getStrikePrice } from '../data/productPricing';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Inject the font styles
if (typeof document !== 'undefined' && !document.head.querySelector('[data-product-card-fonts]')) {
  const styleElement = document.createElement('style');
  styleElement.textContent = fontImport;
  styleElement.setAttribute('data-product-card-fonts', 'true');
  document.head.appendChild(styleElement);
}

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const ProductCard = memo(({ product, viewMode = 'grid' }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isSizeChartOpen, onOpen: onSizeChartOpen, onClose: onSizeChartClose } = useDisclosure();
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedSizeStock, setSelectedSizeStock] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Determine if we're in compact mode (2 columns on mobile)
  const isCompact = viewMode === 'compact';

  const handleAddToCart = async (size) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `/api/cart/add/`,
        {
          product_id: product.id,
          quantity: 1,
          size: size || null,
        }
      );
      dispatch(setCart(response.data));
      toast({
        title: 'Added to cart',
        description: `${product.name} has been added to your cart.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose(); // Close the modal on success
    } catch (error) {
      console.log(error.response);
      if (error.response?.status !== 401) {
        const errorMessage = error.response?.data?.quantity?.[0] || 
                           error.response?.data?.detail || error.response?.data?.error||
                           'Could not add item to cart.';
        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onAddToCartClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (product.sizesAvailable) {
      onOpen();
    } else {
      handleAddToCart();
    }
  };

  const handleSizeChange = (e) => {
    const size = e.target.value;
    setSelectedSize(size);

    if (size && product.sizesAvailable) {
      const sizeIndex = product.sizes.indexOf(parseInt(size));
      const stock = product.stockDivision[sizeIndex];
      setSelectedSizeStock(stock);
    } else {
      setSelectedSizeStock(null);
    }
  };

  const onModalAddToCartClick = () => {
    if (!selectedSize) {
      toast({
        title: 'Please select a size',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    handleAddToCart(selectedSize);
  };

  return (
    <>
      <MotionBox
        as={RouterLink}
        to={`/product/${product.slug}`}
        position="relative"
        borderRadius="2xl"
        overflow="hidden"
        bg="linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
        transition={{ duration: 0.4, ease: "easeOut" }}
        whileHover={{ 
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)",
          translateY: -6,
          scale: 1.02
        }}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        display="flex"
        flexDirection="column"
        height="auto"
        minH={{ 
          base: isCompact ? "300px" : "320px", 
          md: "420px", 
          lg: "450px" 
        }}
        cursor="pointer"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.02) 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        }}
        _hover={{
          _before: {
            opacity: 1,
          }
        }}
        onClick={(e) => {
          // Only prevent default for button clicks to allow RouterLink to work naturally
          if (e.target.tagName === 'BUTTON') {
            e.preventDefault();
          }
        }}
      >
        {/* Product Image Container */}
        <Box 
          position="relative" 
          overflow="hidden"
          borderRadius="xl"
          m={3}
          mb={2}
          boxShadow="0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
          bg="linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)"
        >
          <Image
            src={product.primaryImage || '/no_image_available.jpg'}
            alt={product.name}
            objectFit="cover"
            h={{ 
              base: isCompact ? "180px" : "200px", 
              md: "220px", 
              lg: "240px" 
            }}
            w="100%"
            borderRadius="lg"
            transition="transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{
              transform: "scale(1.05)",
            }}
          />
          {/* Gradient overlay for depth */}
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            h="40%"
            bgGradient="linear(to-t, rgba(0,0,0,0.05), transparent)"
            pointerEvents="none"
          />
          
          {/* Stock Badge */}
          {product.stock === 0 ? (
            <Badge
              position="absolute"
              top="12px"
              right="12px"
              colorScheme="red"
              variant="solid"
              borderRadius="full"
              px={3}
              py={1}
              fontSize="xs"
              fontWeight="600"
              fontFamily="'Inter', sans-serif"
              boxShadow="0 2px 8px rgba(239, 68, 68, 0.3)"
            >
              Out of Stock
            </Badge>
          ) : product.stock < 3 && (
            <Badge
              position="absolute"
              top="12px"
              right="12px"
              colorScheme="orange"
              variant="solid"
              borderRadius="full"
              px={3}
              py={1}
              fontSize="xs"
              fontWeight="600"
              fontFamily="'Inter', sans-serif"
              boxShadow="0 2px 8px rgba(251, 146, 60, 0.3)"
            >
              Only {product.stock} left
            </Badge>
          )}
        </Box>

        {/* Content Container with fixed structure */}
        <Stack 
          spacing={0} 
          p={{ base: isCompact ? 3 : 3, lg: 5 }} 
          pt={{ base: isCompact ? 2 : 2, md: 2 }}
          flex="1"
          justify="space-between"
          minH={{ base: isCompact ? "100px" : "90px", md: "160px" }}
        >
          {/* Product Info Section - Fixed Height */}
          <Box minH={{ base: isCompact ? "50px" : "50px", md: "90px" }} mb={{ base: isCompact ? 1 : 1, md: 3 }}>
            {/* Product Name - Limited to 2-3 lines based on view */}
            <Text 
              fontFamily="'Cormorant', serif"
              fontWeight="700"
              fontSize={{ 
                base: isCompact ? "md" : "xl", 
                md: "2xl" 
              }}
              color="gray.800"
              lineHeight="1.2"
              display="-webkit-box"
              overflow="hidden"
              sx={{
                WebkitLineClamp: isCompact ? 2 : 2,
                WebkitBoxOrient: "vertical",
              }}
              mb={1}
              minH={{ 
                base: isCompact ? "28px" : "28px", 
                md: "58px" 
              }}
              letterSpacing="0.3px"
            >
              {product.name}
            </Text>
            
            {/* Category */}
            <Text 
              fontFamily="'Zain', sans-serif"
              fontSize={{ base: isCompact ? "xs" : "sm", md: "sm" }}
              color="gray.500"
              fontWeight="500"
              textTransform="capitalize"
              letterSpacing="0.5px"
            >
              {product.category}
            </Text>
          </Box>

          {/* Price and Button Section - Always at bottom */}
          <Box>
            <Flex justify="space-between" align="center" mb={{ base: 1, md: 2 }}>
              <Flex direction="column" gap={{ base: 0.5, md: 1 }}>
                {/* Strikethrough Price with Discount Badge */}
                {getStrikePrice(product.id) && (
                  <Flex align="center" gap={2}>
                    <Box position="relative" display="inline-block">
                      <Text 
                        fontFamily="'Cormorant', serif"
                        fontWeight="500" 
                        fontSize={{ 
                          base: isCompact ? "md" : "xl", 
                          md: "xl" 
                        }}
                        color="gray.600"
                        letterSpacing="0.3px"
                        position="relative"
                        _after={{
                          content: '""',
                          position: 'absolute',
                          left: '0',
                          top: '50%',
                          width: '100%',
                          height: '2px',
                          backgroundColor: '#E53E3E',
                          transform: 'translateY(-50%) rotate(-5deg)',
                        }}
                      >
                        ₹{getStrikePrice(product.id)}
                      </Text>
                    </Box>
                    <Badge 
                      bg="#E53E3E"
                      color="white"
                      fontSize={{ base: isCompact ? "2xs" : "xs", md: "xs" }}
                      fontWeight="700"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      fontFamily="'Zain', sans-serif"
                    >
                      {Math.round((1 - product.price / getStrikePrice(product.id)) * 100)}% OFF
                    </Badge>
                  </Flex>
                )}
                {/* Actual Price */}
                <Text 
                  fontFamily="'Cormorant', serif"
                  fontWeight="800" 
                  fontSize={{ 
                    base: isCompact ? "xl" : "2xl", 
                    md: "3xl" 
                  }}
                  color="black"
                  letterSpacing="0.3px"
                  lineHeight="1"
                >
                  ₹{product.price}
                </Text>
              </Flex>
            </Flex>
            
            <MotionButton
              w="full"
              h={{ 
                base: isCompact ? "36px" : "40px", 
                md: "44px" 
              }}
              bg="black"
              color="white"
              fontFamily="'Zain', sans-serif"
              fontWeight="600"
              fontSize={{ 
                base: isCompact ? "xs" : "sm", 
                md: "md" 
              }}
              borderRadius="xl"
              onClick={onAddToCartClick}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.2)"
              }}
              whileTap={{ scale: 0.98 }}
              isDisabled={product.stock === 0}
              _hover={{
                bg: "gray.800",
              }}
              _active={{
                bg: "gray.900",
              }}
              _disabled={{
                bg: "gray.400",
                cursor: "not-allowed",
                _hover: { bg: "gray.400" }
              }}
              boxShadow="0 4px 14px rgba(0, 0, 0, 0.15)"
              border="2px solid"
              borderColor="black"
            >
              {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </MotionButton>
          </Box>
        </Stack>
      </MotionBox>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(4px)" />
        <ModalContent 
          borderRadius="2xl"
          boxShadow="0 25px 50px rgba(0, 0, 0, 0.25)"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.1)"
          bg="linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)"
        >
          <ModalHeader 
            fontFamily="'Playfair Display', serif"
            fontSize="xl"
            fontWeight="600"
            color="gray.800"
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            Select Size
          </ModalHeader>
          <ModalCloseButton 
            color="gray.500"
            _hover={{ bg: "gray.100" }}
          />
          <ModalBody py={6}>
            <Text 
              mb={4} 
              fontFamily="'Inter', sans-serif"
              color="gray.600"
              fontSize="md"
            >
              Please select a size for <Text as="span" fontWeight="600" color="gray.800">{product.name}</Text>
            </Text>
            <Select
              placeholder="Select size"
              value={selectedSize}
              onChange={handleSizeChange}
              borderRadius="xl"
              border="2px solid"
              borderColor="gray.200"
              fontFamily="'Inter', sans-serif"
              _focus={{
                borderColor: "gray.400",
                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
              }}
              _hover={{
                borderColor: "gray.300"
              }}
            >
              {product.sizes?.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </Select>
            {selectedSize && selectedSizeStock !== null && (
              <Text 
                mt={3} 
                color={selectedSizeStock === 0 ? 'red.500' : 'gray.600'} 
                fontWeight="600"
                fontFamily="'Inter', sans-serif"
                fontSize="sm"
              >
                {selectedSizeStock === 0 ? 'Out of stock' : selectedSizeStock === 1 ? `Only 1 left in stock!` : `${selectedSizeStock} available`}
              </Text>
            )}
            <Button 
              mt={4} 
              variant="link" 
              color="gray.600"
              onClick={onSizeChartOpen}
              fontFamily="'Inter', sans-serif"
              fontWeight="500"
              _hover={{ color: "gray.800" }}
            >
              Size Chart
            </Button>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100">
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={onClose}
              fontFamily="'Inter', sans-serif"
              borderRadius="xl"
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
              color="white"
              onClick={onModalAddToCartClick}
              isDisabled={!selectedSize || selectedSizeStock === 0 || isLoading}
              isLoading={isLoading}
              fontFamily="'Inter', sans-serif"
              fontWeight="500"
              borderRadius="xl"
              px={6}
              _hover={{
                bg: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
              }}
              boxShadow="0 4px 14px rgba(0, 0, 0, 0.15)"
            >
              Add to Cart
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isSizeChartOpen} onClose={onSizeChartClose} size="xl">
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" backdropFilter="blur(4px)" />
        <ModalContent 
          borderRadius="2xl"
          boxShadow="0 25px 50px rgba(0, 0, 0, 0.25)"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.1)"
          bg="linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)"
        >
          <ModalHeader 
            fontFamily="'Playfair Display', serif"
            fontSize="xl"
            fontWeight="600"
            color="gray.800"
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            Size Chart
          </ModalHeader>
          <ModalCloseButton 
            color="gray.500"
            _hover={{ bg: "gray.100" }}
          />
          <ModalBody py={6}>
            <Image src="/size-chart.png" alt="Size Chart" borderRadius="xl" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
});

export default ProductCard;